import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    of,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { BusinessDomain } from '../../common/models/business-domain.model';
import { BusinessDomainService } from '../business-domain.service';

type BusinessDomainsSearchForm = {
    search: FormControl<string>;
    returnAll: FormControl<boolean>;
};

@Component({
    selector: 'app-list-business-domains',
    imports: [
        CommonModule,
        RouterModule,
        MatTableModule,
        MatButton,
        NgbModule,
        FormsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatCheckbox,
        MatProgressSpinnerModule,
    ],
    templateUrl: './list-business-domains.component.html',
    styleUrl: './list-business-domains.component.scss',
    standalone: true,
})
export class ListBusinessDomainsComponent implements OnInit {
    protected businessDomains$!: Observable<BusinessDomain[]>;
    protected displayedColumns: string[] = ['name', 'state', 'confidentiality', 'roles', 'actions'];
    protected businessDomainsSearchForm: FormGroup<BusinessDomainsSearchForm>;

    protected loadingResults = signal(false);

    public constructor(
        private readonly businessDomainService: BusinessDomainService,
        private readonly fb: FormBuilder,
    ) {
        this.businessDomainsSearchForm = this.fb.nonNullable.group({
            search: this.fb.nonNullable.control<string>(''),
            returnAll: this.fb.nonNullable.control<boolean>(false),
        });
    }

    public ngOnInit(): void {
        const returnAll$ = this.businessDomainsSearchForm.controls.returnAll.valueChanges.pipe(startWith(false));
        const businessDomains$ = returnAll$.pipe(
            tap(() => this.loadingResults.set(true)),
            switchMap((returnAll) => {
                return this.businessDomainService.listBusinessDomains({ returnAll }).pipe(
                    catchError(() => of([] as BusinessDomain[])),
                    finalize(() => this.loadingResults.set(false)),
                );
            }),
        );

        const search$ = this.businessDomainsSearchForm.controls.search.valueChanges.pipe(
            startWith(''),
            debounceTime(500),
            distinctUntilChanged(),
        );

        this.businessDomains$ = combineLatest([businessDomains$, search$]).pipe(
            map(([businessDomains, search]) => {
                const textToSearch = search.trim().toLowerCase();
                return businessDomains.filter(
                    (businessDomain) =>
                        businessDomain.name.toLowerCase().includes(textToSearch) ||
                        businessDomain.azureDataLakeUri?.toLowerCase().includes(textToSearch),
                );
            }),
        );
    }

    protected getRoles(domain: BusinessDomain): string[] {
        return Object.keys(domain.roles ?? {});
    }
}
